/**
 * useFollowStrategyGeneration — the strategy canvas follows a RUNNING generation.
 *
 * Task G3d (AC-GP-014). Since G3a the server saves the planned blocks right after
 * post-processing (`generationState: 'pending'`) and each block's screens when it is
 * accepted (`'accepted'`, or `'failed'` / `'invalid'`), the head reads
 * `generationStatus: 'running'` with `lastGeneratedTaskId` until the run ends, and a
 * completed regeneration deletes the previous generation's blocks at the very end.
 * The canvas loads blocks imperatively and keeps NO live listener (GP-R22), so an
 * owner watching a run saw nothing until a reload.
 *
 * This hook adds the follow — and only while a run is actually in flight:
 * - nothing is subscribed when no generation runs (a normal editing session is unchanged);
 * - block documents are read through the SERVICE layer (`StrategyBlockService`), never
 *   through a raw `onSnapshot` in a component (CLAUDE.md «Imperative-First Flow»);
 * - blocks that land (and blocks whose `generationState` changed) are refreshed through
 *   the coordinator's existing refresh paths, debounced so the planned-blocks burst of
 *   ~20 documents costs ONE pass — and a burst above the bulk threshold is refreshed
 *   with one collection read instead of N document reads;
 * - a block the owner is editing right now is never refreshed under them — the check is
 *   made again for every block at refresh time, so an editor opened inside the debounce
 *   window still protects it, and the block is picked up by a later snapshot;
 * - the end of the run costs one full canvas refresh — that is what picks up the
 *   previous generation's deletion — and then everything is unsubscribed. A finished run
 *   is never followed again.
 *
 * Identifying the run, in order:
 *  1. the live task store the canvas's tasks panel already connects — a `generate-strategy`
 *     task of THIS strategy whose status is not terminal (zero new listeners; it also
 *     covers a run started in another tab or by the CLI while the canvas is open, and a
 *     head that reads `running` without naming a task);
 *  2. `fallbackTaskId` — the head's `lastGeneratedTaskId` while its `generationStatus` is
 *     `'running'` (canvas opened mid-run), or the task id of a generation started here.
 *
 * Ending the run has THREE signals, because no single one is reliable:
 *  - the task reaches a terminal status;
 *  - the task document disappears (the tasks panel can delete it) — `subscribe` reports
 *    that as `null`, and without treating it as the end the block listener would stay
 *    open for the rest of the session;
 *  - the head stops describing this run (`generationStatus` no longer `'running'`, or
 *    `lastGeneratedTaskId` names another task) — but only once the head has acknowledged
 *    this run, since it still describes the PREVIOUS run until the new one starts.
 *
 * No visual element is added (owner decision 2026-09-11): blocks written as
 * `pending` / `failed` / `invalid` render as the ordinary block nodes they are; the owner
 * follows the run in the canvas's background-tasks panel.
 */

import { useEffect, useRef } from 'react';
import type { BackendTask } from '@segmently/schema-models/strategy';
import { StrategyBlockService } from '@modules/firebase-core/services/StrategyBlockService';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { useTaskStore, taskFirestoreService } from '@modules/background-tasks/store/taskStore';
import { TERMINAL_STATUSES } from '@modules/background-tasks/types';
import type { TaskStatus } from '@modules/background-tasks/types';

/** One pass per burst: the planned blocks of a run are written in one go (G3a). */
export const FOLLOW_GENERATION_DEBOUNCE_MS = 500;

/** A generation writes at most a few dozen blocks; the previous generation is still there too. */
export const FOLLOW_GENERATION_BLOCK_LIMIT = 200;

/** More than this many blocks in one pass: ONE collection read instead of N document reads. */
export const FOLLOW_GENERATION_BULK_THRESHOLD = 8;

export interface UseFollowStrategyGenerationOptions {
  projectId: string;
  strategyId: string;
  /** The canvas is open and its coordinator is ready. */
  enabled: boolean;
  /** Run id to use when the live task store has none: the head's, or this session's own start. */
  fallbackTaskId: string | null;
  /** Refresh ONE block through the coordinator (its existing per-block refresh path). */
  refreshBlock: (blockId: string) => Promise<unknown> | void;
  /** Refresh MANY blocks with one collection read; used above the bulk threshold. */
  refreshBlocks?: (blockIds: string[]) => Promise<unknown> | void;
  /** One full canvas refresh, called once when the run ends. */
  onRunEnd: () => void;
  /** True while the owner has that block open in an editor — it is never refreshed under them. */
  isBlockBusy?: (blockId: string) => boolean;
  /** Test seam; defaults to {@link FOLLOW_GENERATION_DEBOUNCE_MS}. */
  debounceMs?: number;
  /** Test seam; defaults to {@link FOLLOW_GENERATION_BULK_THRESHOLD}. */
  bulkThreshold?: number;
}

/** The generation bookkeeping the server writes on a block doc (G3a; not part of the schema type). */
function readGenerationState(block: unknown): string | null {
  const state = (block as { generationState?: unknown } | null)?.generationState;
  return typeof state === 'string' && state.length > 0 ? state : null;
}

function createdAtMs(task: BackendTask): number {
  const createdAt = task.createdAt as unknown;
  if (createdAt instanceof Date) return createdAt.getTime();
  if (typeof createdAt === 'string') return Date.parse(createdAt) || 0;
  return 0;
}

/**
 * The newest non-terminal `generate-strategy` task of this strategy in the live task
 * store the canvas's tasks panel already connects — no listener of our own.
 */
export function selectRunningGenerationTaskId(
  state: { tasks: Record<string, BackendTask>; projectId: string | null },
  projectId: string,
  strategyId: string,
): string | null {
  if (!projectId || !strategyId || state.projectId !== projectId) return null;

  let running: BackendTask | null = null;
  for (const task of Object.values(state.tasks ?? {})) {
    if (task?.taskType !== 'generate-strategy') continue;
    if (task.strategyId !== strategyId) continue;
    if (TERMINAL_STATUSES.includes(task.status)) continue;
    if (!running || createdAtMs(task) > createdAtMs(running)) running = task;
  }

  return running?.id ?? null;
}

export function useFollowStrategyGeneration(options: UseFollowStrategyGenerationOptions): void {
  const {
    projectId,
    strategyId,
    enabled,
    fallbackTaskId,
    debounceMs = FOLLOW_GENERATION_DEBOUNCE_MS,
    bulkThreshold = FOLLOW_GENERATION_BULK_THRESHOLD,
  } = options;

  // The callbacks are read at call time, so an unstable parent callback never
  // tears the subscription down and back up mid-run.
  const optionsRef = useRef(options);
  useEffect(() => { optionsRef.current = options; });

  // A run is followed at most once: after a reload the head can still name the
  // task that just finished, and re-following it would loop (end → reload → end).
  const finishedTaskIdsRef = useRef<Set<string>>(new Set());

  const storeTaskId = useTaskStore((state) => selectRunningGenerationTaskId(state, projectId, strategyId));
  const taskId = storeTaskId ?? fallbackTaskId;

  useEffect(() => {
    if (!enabled || !projectId || !strategyId || !taskId) return undefined;
    if (finishedTaskIdsRef.current.has(taskId)) return undefined;

    let stopped = false;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    /** The task document proved this run alive at least once (see M3 below). */
    let runSeenAlive = false;
    /** The head already describes THIS run — before that it still describes the previous one. */
    let headAcknowledgedRun = false;
    const pendingRefreshes = new Set<string>();
    /** Last `generationState` this hook acted on, per block id. */
    const seenStates = new Map<string, string>();
    const unsubscribes: Array<() => void> = [];

    const track = (unsubscribe: () => void): void => {
      if (stopped) {
        unsubscribe();
        return;
      }
      unsubscribes.push(unsubscribe);
    };

    const stop = (): void => {
      if (stopped) return;
      stopped = true;
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      pendingRefreshes.clear();
      while (unsubscribes.length > 0) {
        unsubscribes.pop()?.();
      }
    };

    /**
     * `fullRefresh: false` — the run was already over when the follow started, so the
     * canvas's own initial load already showed the final state; a remount would only
     * flash «Loading blocks…» and reset the viewport for nothing.
     */
    const endRun = (fullRefresh: boolean): void => {
      finishedTaskIdsRef.current.add(taskId);
      stop();
      if (fullRefresh) optionsRef.current.onRunEnd();
    };

    /** A block whose editor opened since it was queued is dropped AND unrecorded, so a later snapshot retries it. */
    const stillFree = (blockId: string): boolean => {
      if (!optionsRef.current.isBlockBusy?.(blockId)) return true;
      seenStates.delete(blockId);
      return false;
    };

    const flush = (): void => {
      debounceTimer = null;
      const queued = Array.from(pendingRefreshes);
      pendingRefreshes.clear();

      void (async () => {
        const blockIds = queued.filter(stillFree);
        if (blockIds.length === 0 || stopped) return;

        const refreshBlocks = optionsRef.current.refreshBlocks;
        if (refreshBlocks && blockIds.length > bulkThreshold) {
          try {
            await refreshBlocks(blockIds);
          } catch (error) {
            console.error('[useFollowStrategyGeneration] Failed to refresh blocks', blockIds.length, error);
          }
          return;
        }

        for (const blockId of blockIds) {
          if (stopped) return;
          // Re-checked per block: a sequential pass takes time, and the owner can
          // open an editor in the middle of it.
          if (!stillFree(blockId)) continue;
          try {
            await optionsRef.current.refreshBlock(blockId);
          } catch (error) {
            // One unreadable block never stops the follow.
            console.error('[useFollowStrategyGeneration] Failed to refresh block', blockId, error);
          }
        }
      })();
    };

    const queueRefresh = (blockId: string): void => {
      pendingRefreshes.add(blockId);
      if (debounceTimer) return;
      debounceTimer = setTimeout(flush, debounceMs);
    };

    const blockService = new StrategyBlockService(projectId, strategyId);
    track(blockService.onCollectionSnapshot(
      (blocks) => {
        if (stopped) return;
        for (const block of blocks) {
          const blockId = block?.id;
          if (!blockId) continue;

          // Only this generation's documents: a hand-made block (or a library
          // shell the canvas load deliberately hides) carries no generation state.
          const state = readGenerationState(block);
          if (!state) continue;

          // A block the owner is editing is left alone — and left unseen, so the
          // next snapshot after they close the editor picks it up.
          if (!stillFree(blockId)) continue;

          if (seenStates.get(blockId) === state) continue;
          seenStates.set(blockId, state);
          queueRefresh(blockId);
        }
      },
      (error) => {
        console.error('[useFollowStrategyGeneration] Block snapshot error:', error);
      },
      {
        limit: FOLLOW_GENERATION_BLOCK_LIMIT,
        orderByField: 'createdAt',
        orderDirection: 'desc',
      },
    ));

    track(taskFirestoreService.subscribe(
      projectId,
      taskId,
      (task) => {
        if (stopped) return;
        if (task === null) {
          // The task document is gone (panel delete / bulk delete). Without this the
          // block listener would stay open for the rest of the session.
          endRun(true);
          return;
        }
        if (!TERMINAL_STATUSES.includes(task.status as TaskStatus)) {
          runSeenAlive = true;
          return;
        }
        // The full refresh picks up the final block states AND the previous
        // generation's deletion, which no per-block refresh can see.
        endRun(runSeenAlive);
      },
      (error) => {
        console.error('[useFollowStrategyGeneration] Generation task listener error:', error);
      },
    ));

    const strategyService = new StrategyService(projectId);
    track(strategyService.onSnapshot(
      strategyId,
      (strategy) => {
        if (stopped || !strategy) return;
        const head = strategy as unknown as { generationStatus?: unknown; lastGeneratedTaskId?: unknown };
        if (head.generationStatus === 'running' && head.lastGeneratedTaskId === taskId) {
          headAcknowledgedRun = true;
          runSeenAlive = true;
          return;
        }
        // Until the head names this run it still describes the previous one.
        if (!headAcknowledgedRun) return;
        endRun(true);
      },
      (error) => {
        console.error('[useFollowStrategyGeneration] Strategy head listener error:', error);
      },
    ));

    return stop;
  }, [enabled, projectId, strategyId, taskId, debounceMs, bulkThreshold]);
}
